import { useCallback } from 'react';

import type {
    GlobalSearchRequest,
    SearchRequestCompoundFilter,
    SearchRequestFilter,
    SearchRequestNestedFilter,
} from '../models/models';

import { useAsyncDispatcher } from '../../common/hooks/useAsyncDispatcher';
import { useAppSelector } from '../../common/hooks/useThunkDispatch';
import { storeSelectors } from '../../common/selectors/storeSelectors';
import searchFlow from '../flow/searchFlow';
import { SearchResultType } from '../models/SearchResultType';

export interface SearchResultPageCountPerType {
    products: number;
    recipes: number;
    stores: number;
    articles: number;
    pointshop: number;
}

export type GlobalSearchAbortableParams = {
    query: string;
    currentProductionUnit: string;
    resultCountsPerPage: SearchResultPageCountPerType;
    quickSearch: boolean;
    abortSignal?: AbortSignal;
    excludePointShopItems?: boolean;
};

export type UseGlobalSearchResultStateParams = {
    pageCounts: SearchResultPageCountPerType;
    quickSearch: boolean;
    excludePointShopItems?: boolean;
};

export type GetGlobalSearchSingleTypeRequestParams = {
    searchTerm: string;
    storeId: string;
    selectedPage: number;
    pageSize: number;
    resultType: SearchResultType;
    excludePointShopItems?: boolean;
};

export type UseGlobalSearchSingleTypeStateParams = {
    pageSize: number;
    resultType: SearchResultType;
    page: number;
    excludePointShopItems?: boolean;
};

export type FetchGlobalSearchParams = {
    query: string;
};

const buildPointshopOrFilter = (excludePointShopItems = false): SearchRequestCompoundFilter => {
    const partnerTypeFilter: SearchRequestFilter = {
        type: 'type',
        comparisonMode: 'equals',
        value: 'PartneroffersEntity',
    };
    const filters: SearchRequestNestedFilter[] = [{ and: [partnerTypeFilter] }];

    if (!excludePointShopItems) {
        const now = new Date().toISOString().replace('T', ' ');
        const pointshopValidityStartFilter: SearchRequestFilter = {
            type: 'attribute',
            attributeName: 'pointshop_validityStartDate',
            comparisonMode: 'lessThan',
            value: now,
        };
        const pointshopValidityEndFilter: SearchRequestFilter = {
            type: 'attribute',
            attributeName: 'pointshop_validityEndDate',
            comparisonMode: 'greaterThan',
            value: now,
        };
        const pointshopTypeFilter: SearchRequestFilter = {
            type: 'type',
            value: 'PointshopEntity',
            comparisonMode: 'equals',
        };
        const notHiddenFilter: SearchRequestFilter = {
            type: 'attribute',
            attributeName: 'pointshop_category',
            value: 'Dold kategori',
            comparisonMode: 'equals',
        };

        filters.push({
            and: [
                pointshopValidityEndFilter,
                pointshopValidityStartFilter,
                pointshopTypeFilter,
                { not: notHiddenFilter },
            ],
        });
    }

    return { or: filters };
};

export const globalSearchAbortable = ({
    query,
    currentProductionUnit,
    resultCountsPerPage,
    quickSearch,
    abortSignal,
    excludePointShopItems = false,
}: GlobalSearchAbortableParams) => {
    const searchRequest = {
        page: 0,
        resultsPerPage: 0,
        facets: [],
        sortBy: [],
    };

    const globalSearchRequest: GlobalSearchRequest = {
        searchTerm: query,
        storeId: currentProductionUnit,
        productSearchRequest: {
            ...searchRequest,
            resultsPerPage: resultCountsPerPage.products,
        },
        articleSearchRequest: {
            ...searchRequest,
            resultsPerPage: resultCountsPerPage.articles,
        },
        storeSearchRequest: {
            ...searchRequest,
            resultsPerPage: resultCountsPerPage.stores,
        },
        recipeSearchRequest: {
            ...searchRequest,
            resultsPerPage: resultCountsPerPage.recipes,
        },
        pointshopSearchRequest: {
            page: 0,
            resultsPerPage: resultCountsPerPage.pointshop,
            facets: [],
            sortBy: [],
            filter: buildPointshopOrFilter(excludePointShopItems),
        },
    };

    return searchFlow.searchGlobalLoop(globalSearchRequest, quickSearch, abortSignal);
};

export const useGlobalSearchResultState = ({
    pageCounts,
    quickSearch,
    excludePointShopItems = false,
}: UseGlobalSearchResultStateParams) => {
    const currentProductionUnit = useAppSelector(storeSelectors.currentProductionUnit);
    const { execute, result, error, isLoading, status } = useAsyncDispatcher(
        (_query: string, _currentProductionUnit: string, _quickSearch: boolean) => {
            return globalSearchAbortable({
                query: _query,
                currentProductionUnit: _currentProductionUnit,
                resultCountsPerPage: pageCounts,
                quickSearch: _quickSearch,
                excludePointShopItems,
            });
        },
    );

    const fetch = useCallback(
        ({ query }: FetchGlobalSearchParams) => {
            if (currentProductionUnit) {
                execute(query, currentProductionUnit, quickSearch);
            }
        },
        [currentProductionUnit, quickSearch, execute],
    );
    return {
        result,
        error,
        isLoading,
        fetch,
        status,
    };
};

const getGlobalSearchSingleTypeRequest = ({
    searchTerm,
    storeId,
    selectedPage,
    pageSize,
    resultType,
    excludePointShopItems = false,
}: GetGlobalSearchSingleTypeRequestParams): GlobalSearchRequest => {
    const filledSingleTypeRquest = {
        page: selectedPage,
        resultsPerPage: pageSize,
        facets: [],
        sortBy: [],
    };

    const filledSingleTypeFilterRquest = {
        page: selectedPage,
        resultsPerPage: pageSize,
        facets: [],
        sortBy: [],
        filter: buildPointshopOrFilter(excludePointShopItems),
    };

    const globalSearchRequest: GlobalSearchRequest = {
        searchTerm,
        storeId,
        productSearchRequest:
            resultType === SearchResultType.Products ? filledSingleTypeRquest : undefined,
        articleSearchRequest:
            resultType === SearchResultType.Articles ? filledSingleTypeRquest : undefined,
        pointshopSearchRequest:
            resultType === SearchResultType.Pointshop ? filledSingleTypeFilterRquest : undefined,
        storeSearchRequest:
            resultType === SearchResultType.Stores ? filledSingleTypeRquest : undefined,
        recipeSearchRequest:
            resultType === SearchResultType.Recipes ? filledSingleTypeRquest : undefined,
        recipeCategoriesSearchRequest:
            resultType === SearchResultType.Recipes
                ? { ...filledSingleTypeRquest, page: 0 }
                : undefined, // every time we fetch recipes in GlobalSearchState, we also want recipe categories
    };

    return globalSearchRequest;
};

export const useGlobalSearchSingleTypeState = ({
    pageSize,
    resultType,
    page,
    excludePointShopItems = false,
}: UseGlobalSearchSingleTypeStateParams) => {
    const currentProductionUnit = useAppSelector(storeSelectors.currentProductionUnit);
    const { execute, result, error, isLoading, status } = useAsyncDispatcher(
        (_query: string, _selectedPage: number, _currentProductionUnit: string) => {
            return searchFlow.searchGlobalLoop(
                getGlobalSearchSingleTypeRequest({
                    searchTerm: _query,
                    storeId: _currentProductionUnit,
                    selectedPage: _selectedPage,
                    pageSize,
                    resultType,
                    excludePointShopItems,
                }),
                false,
                undefined,
            );
        },
        { keepPreviousResult: true },
    );

    const fetch = useCallback(
        ({ query }: FetchGlobalSearchParams) => {
            if (currentProductionUnit) {
                execute(query, page, currentProductionUnit);
            }
        },
        [currentProductionUnit, execute, page],
    );

    return {
        isLoading,
        result,
        error,
        fetch,
        status,
    };
};
